import React from 'react';
import { Calendar, ExternalLink } from 'lucide-react';
import Section from '../Section';
import Breadcrumb from '../common/Breadcrumb';
import { NewsDetailData } from './types';

interface Props {
  data: NewsDetailData;
}

const NewsDetailLayout: React.FC<Props> = ({ data }) => {
  const BadgeIcon = data.badgeIcon;

  return (
    <div className="min-h-screen">
      {/* ヒーロー */}
      <section className="relative pt-40 pb-24 overflow-hidden bg-[#1B2A4A]">
        <div className="absolute inset-0 bg-[#4A6FA5]/10" />
        <div className="relative mx-auto max-w-6xl px-5 lg:px-6">
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/40 bg-[#B8860B]/10 px-4 py-1.5 mb-8">
              <BadgeIcon className="w-4 h-4 text-[#B8860B]" />
              <span className="text-sm font-semibold text-[#B8860B]">{data.badgeText}</span>
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-[3.3rem] font-bold text-center text-[#F5F3EF] mb-6" style={{ lineHeight: '1.5' }}>
            {data.title}
          </h1>
          <div className="flex items-center justify-center gap-2 text-[#F5F3EF]/60">
            <Calendar className="w-4 h-4" />
            <time className="text-sm">{data.date}</time>
          </div>
        </div>
      </section>

      <Breadcrumb items={[
        { label: 'ニュース', path: '/company/news' },
        { label: data.breadcrumbLabel }
      ]} />

      <Section className="py-16 lg:py-20 bg-[#F5F3EF]">
        <div className="mx-auto max-w-4xl px-5 lg:px-6">
          {/* リード文 */}
          <div className="rounded-xl border-l-4 border-[#B8860B] bg-white px-6 py-5 mb-10">
            <p className="text-[#2D3748] font-medium text-justify leading-relaxed">
              {data.lead}
            </p>
          </div>

          {/* サービス概要 */}
          <h2 className="text-xl font-bold text-[#2D3748] mt-10 mb-4">サービス概要</h2>
          {data.overviewParagraphs.map((paragraph, i) => (
            <p key={i} className={`text-[#5A6577] leading-relaxed text-justify ${i < data.overviewParagraphs.length - 1 ? 'mb-6' : 'mb-10'}`}>
              {paragraph}
            </p>
          ))}

          {/* 主な機能 */}
          <h2 className="text-xl font-bold text-[#2D3748] mt-10 mb-6">主な機能</h2>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {data.features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="rounded-2xl border border-[#E8E6E1] bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#1B2A4A]/10">
                      <Icon className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#2D3748]">{feature.title}</h3>
                  </div>
                  <p className="text-[#5A6577] leading-relaxed text-justify">{feature.description}</p>
                </div>
              );
            })}
          </div>

          {/* 活用シーン */}
          <h2 className="text-xl font-bold text-[#2D3748] mt-10 mb-6">活用シーン</h2>
          <div className="rounded-2xl border border-[#E8E6E1] bg-white p-6 shadow-sm mb-10">
            <ul className="space-y-3">
              {data.useCases.map((useCase, index) => (
                <li key={index} className="flex items-center text-[#5A6577]">
                  <div className="w-1.5 h-1.5 bg-[#B8860B] rounded-full mr-3 flex-shrink-0" />
                  {useCase}
                </li>
              ))}
            </ul>
          </div>

          {/* 料金プラン（任意） */}
          {data.pricing && (
            <>
              <h2 className="text-xl font-bold text-[#2D3748] mt-10 mb-6">料金プラン</h2>
              <div className="rounded-2xl border border-[#E8E6E1] bg-white overflow-hidden shadow-sm mb-10">
                <div className={`grid ${data.pricing.plans.length === 1 ? '' : data.pricing.plans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                  {data.pricing.plans.map((plan, index) => (
                    <div
                      key={index}
                      className={`p-6 text-center ${
                        index < data.pricing!.plans.length - 1
                          ? 'border-b md:border-b-0 md:border-r border-[#E8E6E1]'
                          : ''
                      }`}
                    >
                      <h3 className="text-lg font-bold text-[#2D3748] mb-2">{plan.title}</h3>
                      <div className={`text-3xl font-bold mb-1 ${plan.highlight ? 'text-[#B8860B]' : 'text-[#1B2A4A]'}`}>
                        {plan.price}
                      </div>
                      <p className="text-sm text-[#7B8794]">{plan.note}</p>
                    </div>
                  ))}
                </div>
                {data.pricing.footer && (
                  <div className="border-t border-[#E8E6E1] px-6 py-3 text-center bg-[#FAFAF8]">
                    <p className="text-sm text-[#5A6577]">{data.pricing.footer}</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* 今後の展開 */}
          <h2 className="text-xl font-bold text-[#2D3748] mt-10 mb-4">今後の展開</h2>
          <p className="text-[#5A6577] leading-relaxed mb-10 text-justify">
            {data.roadmap}
          </p>

          {/* 代表コメント */}
          <h2 className="text-xl font-bold text-[#2D3748] mt-10 mb-4">代表コメント</h2>
          <div className="rounded-xl border-l-4 border-[#1B2A4A] bg-white px-6 py-5 mb-10">
            <p className="text-[#5A6577] leading-relaxed italic mb-3 text-justify">
              「{data.quote.text}」
            </p>
            <p className="text-sm text-[#7B8794] font-medium">
              ― {data.quote.attribution}
            </p>
          </div>

          {/* CTA */}
          <div className="rounded-2xl bg-[#1B2A4A] p-8 text-center">
            <h3 className="text-2xl font-bold text-[#F5F3EF] mb-3">{data.cta.heading}</h3>
            <p className="text-[#F5F3EF]/70 mb-6">{data.cta.subtext}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={data.cta.primaryButton.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#B8860B] px-8 py-3 text-white font-semibold hover:bg-[#A0750A] transition-colors shadow-lg shadow-[#B8860B]/20"
              >
                <ExternalLink className="w-5 h-5" />
                {data.cta.primaryButton.label}
              </a>
              {data.cta.secondaryButton && (
                <a
                  href={data.cta.secondaryButton.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-[#F5F3EF]/30 px-8 py-3 text-[#F5F3EF] font-semibold hover:bg-white/10 transition-colors"
                >
                  {data.cta.secondaryButton.label}
                </a>
              )}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default NewsDetailLayout;
